import { HandshakeIcon } from 'lucide-react';

import React, { useMemo } from 'react';

import type { ImportCategoryRow } from '@splitfiesta/core';
import { PAYMENT_OVERRIDE, sortedTaxonomyPaths } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { getInputClasses } from '@/lib/styles/form';
import { cn } from '@/lib/utils';

import { getExpenseCategoryIcon } from '@/utils/expenseCategoryIcons';

interface ImportCategoryOverridesProps {
  categories: ImportCategoryRow[];
  /**
   * Normalised Splitwise label → taxonomy path or `PAYMENT_OVERRIDE`, exactly
   * as the payload keys it.
   */
  overrides: Record<string, string>;
  onChange: (key: string, path: string) => void;
}

/** One source category per row, with the app category it will be filed under. */
export function ImportCategoryOverrides({
  categories,
  overrides,
  onChange,
}: ImportCategoryOverridesProps) {
  const { t, i18n } = useTranslation();

  const pathLabel = (path: string) => {
    const [category, subcategory] = path.split('/');
    return subcategory
      ? t(`expenses:subcategories.${category}.${subcategory}`)
      : t(`expenses:categories.${category}`);
  };
  // Categories A–Z by label, each with its subcategories A–Z, "other" last,
  // the same order the mobile picker shows.
  const groups = useMemo(() => {
    const paths = sortedTaxonomyPaths(pathLabel, i18n.language);
    const result: Array<{ category: string; subcategories: string[] }> = [];
    for (const path of paths) {
      if (path.includes('/'))
        result[result.length - 1].subcategories.push(path);
      else result.push({ category: path, subcategories: [] });
    }
    return result;
    // `t` changes when the expenses bundle lands, which is when the labels
    // (and so the order) change; `pathLabel` is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);

  return (
    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
      {categories.map((category, index) => {
        // The normalised label can hold spaces, so it names the test id but
        // never the element id.
        const selectId = `import-review-category-field-${index}`;
        // `hasOwn` rather than a bare lookup: the key is a CSV cell, so a
        // category literally named `constructor` would otherwise resolve up
        // the prototype chain.
        const selected = Object.hasOwn(overrides, category.key)
          ? overrides[category.key]
          : category.mapped;
        const [selectedCategory, selectedSubcategory] = selected.split('/');
        const Icon =
          selected === PAYMENT_OVERRIDE
            ? HandshakeIcon
            : getExpenseCategoryIcon(selectedCategory, selectedSubcategory);

        return (
          <li
            key={category.key}
            className="flex flex-wrap items-center gap-3 px-4 py-3"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-500/10">
              <Icon className="h-5 w-5 text-primary-500" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <label
                htmlFor={selectId}
                className="block truncate text-base text-gray-700 dark:text-gray-200"
              >
                {category.label}
              </label>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('import:categories.rows', { count: category.rows })}
              </span>
              {category.unknown && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                  {t('import:categories.unknownBadge')}
                </span>
              )}
            </div>

            <select
              id={selectId}
              data-testid={`import-review-category-${category.key}`}
              value={selected}
              onChange={event => onChange(category.key, event.target.value)}
              className={cn(
                getInputClasses(),
                'w-full font-medium sm:w-auto sm:max-w-xs'
              )}
            >
              <option value={PAYMENT_OVERRIDE}>
                {t('import:categories.paymentOption')}
              </option>
              {groups.map(group => (
                <optgroup
                  key={group.category}
                  label={t(`expenses:categories.${group.category}`)}
                >
                  <option value={group.category}>
                    {t(`expenses:categories.${group.category}`)}
                  </option>
                  {group.subcategories.map(path => (
                    <option key={path} value={path}>
                      {pathLabel(path)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </li>
        );
      })}
    </ul>
  );
}
